import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { LogService } from '../util/log.service';

export interface BrevoMessageRequest {
  email_address: string;
  name: string;
  template_id: number;
  params: Record<string, any>;
}

export interface BrevoMessageResponse {
  success: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class BrevoService {
  constructor(
    private readonly http: HttpClient,
    private readonly log: LogService,
    private readonly apiService: ApiService
  ) {}

  /**
   * Send a Brevo email using a template
   */
  private sendBrevoMessage(request: BrevoMessageRequest): Observable<BrevoMessageResponse> {
    this.log.debug('Sending Brevo message to:', request.email_address, 'Template ID:', request.template_id);
    const brevoApiUrl = this.apiService.getUrlFor('/admin/api/brevomessage');

    // API returns plain text "OK" on success, not JSON
    return this.http.post(brevoApiUrl, request, { responseType: 'text' }).pipe(
      map((response: string) => {
        // Convert plain text response to our expected format
        if (response === 'OK') {
          return { success: true, message: 'OK' };
        } else {
          return { success: false, message: response };
        }
      })
    );
  }

  /**
   * Send check-in email using Brevo template
   * Template IDs: en=5, hu=6
   */
  sendCheckinEmail(emailAddress: string, guestName: string | undefined, checkinUrl: string, language: 'en' | 'hu'): Observable<BrevoMessageResponse> {
    const templateId = language === 'hu' ? 6 : 5;

    const params: Record<string, any> = {
      url: checkinUrl
    };

    // Only include name parameter if guest name is provided
    // If omitted, Brevo will use fallback "Guest"
    if (guestName) {
      params['name'] = guestName;
    }

    const request: BrevoMessageRequest = {
      email_address: emailAddress,
      name: guestName || 'Guest', // Fallback for the main name field
      template_id: templateId,
      params: params
    };

    return this.sendBrevoMessage(request);
  }

  /**
   * Send parking email using Brevo template
   * Template IDs: en=53, hu=51
   */
  sendParkingEmail(emailAddress: string, guestName: string | undefined, parkingUrl: string, language: 'en' | 'hu'): Observable<BrevoMessageResponse> {
    const templateId = language === 'hu' ? 51 : 53;

    const params: Record<string, any> = {
      url: parkingUrl
    };

    if (guestName) {
      params['name'] = guestName;
    }

    const request: BrevoMessageRequest = {
      email_address: emailAddress,
      name: guestName || 'Guest',
      template_id: templateId,
      params: params
    };

    return this.sendBrevoMessage(request);
  }
}
